import { useState } from "react"
import { DataTypeList, useDataTypesHooks } from "../../../../../features/data-types"
import { Column, Data, FieldType, FieldValue, SpinnerDiv, UIPermission, useUIActions, ModalId, Action } from "../../../../../features/ui"
import { useSystemStateHooks } from "../../../../system"
import { DataType } from "../../../../../tables"
import { CustomerType } from "../../../state/types"
import { CustomerTypeFilters } from "./customerTypeFilters"
import { CreateCustomerTypeModal } from "../create-customer-type-modal/createCustomerTypeModal"
import { DownloadPriceListModal } from "../download-price-list-modal/downloadPriceListModal"
import { useNavigate } from "react-router-dom"
import { EditCustomerTypeModal } from "../edit-customer-type-modal/editCustomerTypeModal"

type Props = {
    customerTypes: CustomerType[]
}

export const CustomerTypeList = (props: Props) => {
    const { customerTypes } = props

    const [rows, setRows] = useState<Data[]>([])
    const [fullData, setFullData] = useState<CustomerType[]>([])
    const [asyncDataLoaded, setAsyncDataLoaded] = useState(false)
    const [selectedCustomerType, setSelectedCustomerType] = useState<CustomerType>()

    const { formatNumber } = useDataTypesHooks()
    const { userHasPermission } = useSystemStateHooks()
    const readOnly = !userHasPermission(UIPermission.SAVE_CUSTOMER_TYPES)

    const navigate = useNavigate()
    const { openModal, toggleModal } = useUIActions()

    const columns: Column[] = [
        {
            name: 'name',
            type: FieldType.TEXT,
            label: 'Nombre'
        },
        {
            name: 'yield',
            type: FieldType.PERCENTAGE,
            label: 'Ajuste General',
            render: (value?: FieldValue) => {
                const paymentTypeYield = value as number
                const absYield = Math.abs(paymentTypeYield)
                return <>{formatNumber(absYield)}% de {paymentTypeYield <= 0 ? 'Descuento' : 'Recargo'}</>
            }
        },
        {
            name: 'adjustmentByProduct',
            type: FieldType.BOOLEAN,
            label: 'Ajuste por Producto'
        }
    ]

    const onView = (id?: string) => {
        const ct = fullData.find(f => f.id === id)
        if (ct) {
            setSelectedCustomerType(ct)
            ct && navigate (`${ct.id}`)
        }
    }

    const onEdit = (id?: string) => {
        const ct = fullData.find(f => f.id === id)
        if (ct) {
            setSelectedCustomerType(ct)
            setTimeout(() => {
                openModal(ModalId.EDIT_CUSTOMER_TYPE);
            }, 0); 
        }
    }

    const onDownload = (id?: string) => {
        const ct = fullData.find(f => f.id === id)
        if (ct) {
            setSelectedCustomerType(ct)
            openModal(ModalId.DOWNLOAD_CUSTOMER_TYPE_PDF)
        }
    }

    const onDelete = (id?: string) => {
        const ct = fullData.find(f => f.id === id)
        if (ct) {
            setSelectedCustomerType(ct)
            setTimeout(() => {
                toggleModal(ModalId.REMOVE)
            }, 0)
        }
    }

    const getRowActions = (rowIdValue: string): Action[] => {
        const actionList: Action[] = [
            { icon: 'eye-fill', kind: 'tertiary', tooltip: 'Ver Detalle', onClick: onView },
            { icon: 'download', kind: 'tertiary', tooltip: 'Descargar Lista', onClick: onDownload }
        ]

        const archived = fullData.find(f => f.id === rowIdValue)?.archived
        if (!readOnly && !archived) {
            actionList.push({ 
                icon: 'pencil-fill', 
                tooltip: 'Editar', 
                onClick: onEdit 
            })
            actionList.push({ 
                icon: 'trash-fill', 
                tooltip: 'Eliminar', 
                kind: 'secondary',
                onClick: onDelete 
            })
        }

        return actionList
    }

    const onSearchStart = () => setAsyncDataLoaded(false)

    const onSearchDone = (dataTypes: DataType[]) => {
        const data = dataTypes as CustomerType[]
        setFullData(data) 
        
        const newRows = data.map(ct => {
            const { productYields, ...rest } = ct
            return {
                ...rest,
                adjustmentByProduct: productYields.length > 0
            } as unknown as Data
        })
        setRows(newRows)
        setAsyncDataLoaded(true)
    }

    const renderAsync = (element: JSX.Element) => asyncDataLoaded ? element : <SpinnerDiv />

    const onCreate = () => openModal(ModalId.CREATE_CUSTOMER_TYPE)

    return (
        <>
            <CustomerTypeFilters
                customerTypes={customerTypes}
                onSearchStart={onSearchStart}
                onSearchDone={onSearchDone}
            />
            {renderAsync(
                <DataTypeList
                    columns={columns}
                    rows={rows}
                    readOnly={readOnly}
                    minItems={1}
                    currentDataTypeParam={selectedCustomerType}
                    onCreate={onCreate}
                    onEdit={onEdit}
                    actions={getRowActions}
                />
            )}
            <CreateCustomerTypeModal />
            <EditCustomerTypeModal customerType={selectedCustomerType} />
            <DownloadPriceListModal customerType={selectedCustomerType} />
        </>
    )
}
